// Vinculum i18n — lightweight translation dictionary + hook (EN / DE / FR / NL)
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";

export const LOCALES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "nl", label: "Nederlands" },
];

const dict = {
  en: {
    nav: { why: "Why", features: "Features", pricing: "Pricing", how: "How it works", markets: "Markets", cta: "Get started" },
    hero: {
      eyebrow: "Built for restaurants & cafés in Europe",
      title_a: "A website that makes your",
      title_em: "restaurant",
      title_b: "feel as good as the food.",
      sub: "Branded websites, reservations and live menus — delivered as a subscription. No setup fee. Launched in 14 days. Cancel any time.",
      cta_primary: "Book an intro call",
      cta_secondary: "See pricing",
      stat1_v: "€0", stat1_l: "Setup fee",
      stat2_v: "14 days", stat2_l: "Until you go live",
      stat3_v: "0%", stat3_l: "Commission on reservations",
    },
    why: {
      eyebrow: "Why a website matters",
      title: "Your reputation no longer lives on the high street. It lives online.",
      lead: "Guests today decide where to eat before they leave the house. Without your own website, you hand that decision — and the margin — to Deliveroo, TheFork and Google reviews.",
      s1_v: "67%", s1_l: "of diners prefer ordering from a restaurant's own website over Deliveroo or TheFork.",
      s2_v: "31%", s2_l: "of European restaurants still have no website — a first-mover opportunity for the rest.",
      s3_v: "0%", s3_l: "commission on reservations made through your Vinculum site.",
    },
    features: {
      eyebrow: "What you get",
      title: "Six essentials. One subscription.",
      lead: "Everything an independent restaurant needs to look credible, get found, and own its guest relationship.",
      f1_t: "Branded website",
      f1_d: "An editorial, mobile-first site that mirrors the tone of your room — not a template.",
      f2_t: "Online reservations",
      f2_d: "Take bookings directly. Zero commission. No more paying per cover to third parties.",
      f3_t: "Live menu editor",
      f3_d: "Update dishes, prices and allergens yourself — no developer, no waiting.",
      f4_t: "SEO & local discovery",
      f4_d: "Google Maps, schema markup and local SEO so neighbours actually find you.",
      f5_t: "Mobile-first design",
      f5_d: "Eight out of ten guests will visit on a phone. We design for that first.",
      f6_t: "GDPR compliant",
      f6_d: "Cookie consent, hosting in the EU, privacy-by-design. Built for European rules.",
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Simple monthly pricing. No setup fees.",
      lead: "Choose the tier that fits where you are today. Move up when you're ready.",
      per_month: "/month",
      most_popular: "Most popular",
      cta: "Book a call",
      launch_in: "Launched in",
      days: "days",
      starter_t: "Starter",
      starter_d: "A clean, credible single-page site for places that just need to be found.",
      growth_t: "Growth",
      growth_d: "Reservations, multi-language and analytics — the full digital front door.",
      pro_t: "Pro",
      pro_d: "Multi-location groups with POS, delivery integrations and a dedicated manager.",
    },
    how: {
      eyebrow: "How it works",
      title: "Four steps from kitchen to online.",
      s1_t: "Tell us about your place",
      s1_d: "A 30-minute call. Your story, your menu, your photos, your audience.",
      s2_t: "We design your site",
      s2_d: "An editorial draft, on your domain, within days — not months.",
      s3_t: "Launch & connect",
      s3_d: "Reservations, Google Maps and SEO are wired in. We handle the technical bits.",
      s4_t: "Manage & grow",
      s4_d: "Edit the menu yourself. We optimise, support and improve the site monthly.",
    },
    lead: {
      eyebrow: "Get started",
      title: "Two ways to begin.",
      lead: "Book an intro call on our calendar, or send a quick note and we'll come back the same working day.",
      calendly_t: "Book an intro call",
      calendly_d: "30 minutes. No prep needed.",
      open_calendly: "Open calendar",
      form_t: "Or send us a note",
      f_name: "Your name",
      f_email: "Email",
      f_restaurant: "Restaurant or café",
      f_city: "City",
      f_country: "Country",
      f_plan: "Interested in",
      f_message: "Tell us what you're looking for",
      submit: "Send enquiry",
      sending: "Sending…",
      success_t: "Thank you",
      success_d: "We've received your note and will be in touch within one working day.",
      error: "Something went wrong. Please try again or email Kshitij.shah@thevinculum.de.",
      no_pref: "No preference",
    },
    cta: {
      eyebrow: "Ready when you are",
      title: "Your restaurant deserves a website that does it justice.",
      sub: "No setup fee. Cancel any time. Live in 14 days.",
      primary: "Book an intro call",
    },
    footer: {
      tag: "Branded websites, reservations and SEO for independent restaurants and cafés across North and Mid Europe.",
      product: "Product",
      company: "Company",
      legal: "Legal",
      l_why: "Why Vinculum",
      l_features: "Features",
      l_pricing: "Pricing",
      l_how: "How it works",
      l_about: "About",
      l_contact: "Contact",
      l_book: "Book a call",
      l_privacy: "Privacy",
      l_terms: "Terms",
      l_gdpr: "GDPR",
      copyright: "Vinculum. Built for European hospitality.",
      markets: "Serving North & Mid Europe — Netherlands, Germany, Belgium, France, Austria, Denmark, Sweden.",
    },
  },
  de: {
    nav: { why: "Warum", features: "Funktionen", pricing: "Preise", how: "So funktioniert's", markets: "Märkte", cta: "Loslegen" },
    hero: {
      eyebrow: "Gebaut für Restaurants & Cafés in Europa",
      title_a: "Eine Website, die Ihr",
      title_em: "Restaurant",
      title_b: "so gut wirken lässt wie das Essen.",
      sub: "Markenwebsites, Reservierungen und Live-Menüs — als Abonnement geliefert. Keine Einrichtungsgebühr. In 14 Tagen online. Jederzeit kündbar.",
      cta_primary: "Erstgespräch buchen",
      cta_secondary: "Preise ansehen",
      stat1_v: "€0", stat1_l: "Einrichtungsgebühr",
      stat2_v: "14 Tage", stat2_l: "Bis Sie online sind",
      stat3_v: "0%", stat3_l: "Provision auf Reservierungen",
    },
    why: {
      eyebrow: "Warum eine Website zählt",
      title: "Ihr Ruf lebt nicht mehr in der Einkaufsstraße. Er lebt online.",
      lead: "Gäste entscheiden heute, wo sie essen, bevor sie das Haus verlassen. Ohne eigene Website überlassen Sie diese Entscheidung — und die Marge — Deliveroo, TheFork und Google-Bewertungen.",
      s1_v: "67%", s1_l: "der Gäste bestellen lieber auf der eigenen Website eines Restaurants als bei Deliveroo oder TheFork.",
      s2_v: "31%", s2_l: "der europäischen Restaurants haben noch keine Website — ein klarer Vorsprung für alle anderen.",
      s3_v: "0%", s3_l: "Provision auf Reservierungen über Ihre Vinculum-Seite.",
    },
    features: {
      eyebrow: "Was Sie erhalten",
      title: "Sechs Essentials. Ein Abo.",
      lead: "Alles, was ein unabhängiges Restaurant braucht, um glaubwürdig zu wirken, gefunden zu werden und die Gästebeziehung zu besitzen.",
      f1_t: "Markenwebsite", f1_d: "Eine redaktionelle, mobil-zuerst gestaltete Seite — kein Template.",
      f2_t: "Online-Reservierungen", f2_d: "Direkte Buchungen. Null Provision. Kein Bezahlen pro Gast an Dritte.",
      f3_t: "Live-Menü-Editor", f3_d: "Gerichte, Preise und Allergene selbst aktualisieren — ohne Entwickler.",
      f4_t: "SEO & lokale Sichtbarkeit", f4_d: "Google Maps, Schema-Markup und lokales SEO — damit Nachbarn Sie finden.",
      f5_t: "Mobile-first", f5_d: "Acht von zehn Gästen kommen per Handy. Dafür gestalten wir zuerst.",
      f6_t: "DSGVO-konform", f6_d: "Cookie-Einwilligung, EU-Hosting, Privacy-by-Design. Für europäische Regeln gebaut.",
    },
    pricing: {
      eyebrow: "Preise", title: "Einfache Monatspreise. Keine Einrichtungsgebühren.",
      lead: "Wählen Sie das Paket, das zu Ihrer aktuellen Phase passt. Sie können jederzeit upgraden.",
      per_month: "/Monat", most_popular: "Am beliebtesten", cta: "Gespräch buchen",
      launch_in: "Online in", days: "Tagen",
      starter_t: "Starter", starter_d: "Eine klare, glaubwürdige Einseiten-Site — für Orte, die einfach gefunden werden müssen.",
      growth_t: "Growth", growth_d: "Reservierungen, Mehrsprachigkeit und Analyse — die volle digitale Eingangstür.",
      pro_t: "Pro", pro_d: "Mehrstandort-Gruppen mit POS, Lieferintegrationen und persönlichem Ansprechpartner.",
    },
    how: {
      eyebrow: "So funktioniert's", title: "Vier Schritte von der Küche ins Netz.",
      s1_t: "Erzählen Sie uns von Ihrem Ort", s1_d: "Ein 30-Minuten-Gespräch. Ihre Geschichte, Ihr Menü, Ihre Fotos, Ihr Publikum.",
      s2_t: "Wir gestalten Ihre Seite", s2_d: "Ein redaktioneller Entwurf, auf Ihrer Domain, innerhalb von Tagen.",
      s3_t: "Launch & Anbindung", s3_d: "Reservierungen, Google Maps und SEO werden eingerichtet. Wir kümmern uns um die Technik.",
      s4_t: "Pflegen & wachsen", s4_d: "Menü selbst bearbeiten. Wir optimieren, unterstützen und verbessern monatlich.",
    },
    lead: {
      eyebrow: "Loslegen", title: "Zwei Wege, um zu beginnen.",
      lead: "Buchen Sie ein Erstgespräch in unserem Kalender oder schreiben Sie kurz — wir melden uns innerhalb eines Werktags.",
      calendly_t: "Erstgespräch buchen", calendly_d: "30 Minuten. Keine Vorbereitung nötig.",
      open_calendly: "Kalender öffnen",
      form_t: "Oder schreiben Sie uns",
      f_name: "Ihr Name", f_email: "E-Mail", f_restaurant: "Restaurant oder Café",
      f_city: "Stadt", f_country: "Land", f_plan: "Interesse an", f_message: "Was suchen Sie?",
      submit: "Anfrage senden", sending: "Senden…",
      success_t: "Danke", success_d: "Wir haben Ihre Nachricht erhalten und melden uns innerhalb eines Werktags.",
      error: "Etwas ist schiefgelaufen. Bitte erneut versuchen oder Kshitij.shah@thevinculum.de schreiben.",
      no_pref: "Keine Präferenz",
    },
    cta: {
      eyebrow: "Wann immer Sie bereit sind", title: "Ihr Restaurant verdient eine Website, die ihm gerecht wird.",
      sub: "Keine Einrichtungsgebühr. Jederzeit kündbar. In 14 Tagen live.", primary: "Erstgespräch buchen",
    },
    footer: {
      tag: "Markenwebsites, Reservierungen und SEO für unabhängige Restaurants und Cafés in Nord- und Mitteleuropa.",
      product: "Produkt", company: "Unternehmen", legal: "Rechtliches",
      l_why: "Warum Vinculum", l_features: "Funktionen", l_pricing: "Preise", l_how: "So funktioniert's",
      l_about: "Über uns", l_contact: "Kontakt", l_book: "Gespräch buchen",
      l_privacy: "Datenschutz", l_terms: "AGB", l_gdpr: "DSGVO",
      copyright: "Vinculum. Gebaut für europäische Gastronomie.",
      markets: "Für Nord- & Mitteleuropa — Niederlande, Deutschland, Belgien, Frankreich, Österreich, Dänemark, Schweden.",
    },
  },
  fr: {
    nav: { why: "Pourquoi", features: "Fonctionnalités", pricing: "Tarifs", how: "Comment ça marche", markets: "Marchés", cta: "Commencer" },
    hero: {
      eyebrow: "Conçu pour les restaurants et cafés d'Europe",
      title_a: "Un site qui fait paraître votre",
      title_em: "restaurant",
      title_b: "aussi soigné que sa cuisine.",
      sub: "Sites de marque, réservations et menus en direct — livrés en abonnement. Sans frais d'installation. En ligne en 14 jours. Sans engagement.",
      cta_primary: "Réserver un appel",
      cta_secondary: "Voir les tarifs",
      stat1_v: "€0", stat1_l: "Frais d'installation",
      stat2_v: "14 jours", stat2_l: "Avant la mise en ligne",
      stat3_v: "0%", stat3_l: "Commission sur les réservations",
    },
    why: {
      eyebrow: "Pourquoi un site web",
      title: "Votre réputation ne se joue plus dans la rue. Elle se joue en ligne.",
      lead: "Les clients décident où manger avant même de sortir. Sans site, vous laissez cette décision — et la marge — à Deliveroo, TheFork et aux avis Google.",
      s1_v: "67%", s1_l: "des clients préfèrent commander sur le site du restaurant plutôt que Deliveroo ou TheFork.",
      s2_v: "31%", s2_l: "des restaurants européens n'ont toujours pas de site — une vraie longueur d'avance.",
      s3_v: "0%", s3_l: "de commission sur les réservations via votre site Vinculum.",
    },
    features: {
      eyebrow: "Ce que vous obtenez", title: "Six essentiels. Un abonnement.",
      lead: "Tout ce qu'il faut à un restaurant indépendant pour paraître crédible, se faire trouver et garder la relation client.",
      f1_t: "Site de marque", f1_d: "Une page éditoriale, pensée mobile-first — pas un modèle.",
      f2_t: "Réservations en ligne", f2_d: "Réservations directes. Zéro commission. Plus de paiement au couvert.",
      f3_t: "Éditeur de menu en direct", f3_d: "Mettez à jour plats, prix et allergènes vous-même — sans développeur.",
      f4_t: "SEO & recherche locale", f4_d: "Google Maps, balisage et SEO local pour que vos voisins vous trouvent.",
      f5_t: "Mobile-first", f5_d: "Huit clients sur dix viennent par téléphone. On conçoit pour eux d'abord.",
      f6_t: "Conforme RGPD", f6_d: "Consentement cookies, hébergement UE, privacy-by-design.",
    },
    pricing: {
      eyebrow: "Tarifs", title: "Mensuel et simple. Aucun frais d'installation.",
      lead: "Choisissez le palier qui correspond à votre stade. Évoluez quand vous voulez.",
      per_month: "/mois", most_popular: "Le plus choisi", cta: "Réserver un appel",
      launch_in: "En ligne en", days: "jours",
      starter_t: "Starter", starter_d: "Une page propre et crédible pour les lieux qui veulent simplement être trouvés.",
      growth_t: "Growth", growth_d: "Réservations, multilingue et analytique — la vraie porte d'entrée digitale.",
      pro_t: "Pro", pro_d: "Groupes multi-sites avec POS, livraisons et chargé de compte dédié.",
    },
    how: {
      eyebrow: "Comment ça marche", title: "Quatre étapes, de la cuisine au web.",
      s1_t: "Parlez-nous de votre lieu", s1_d: "Un appel de 30 minutes. Votre histoire, votre carte, vos photos, votre public.",
      s2_t: "On dessine votre site", s2_d: "Une maquette éditoriale, sur votre domaine, en quelques jours.",
      s3_t: "Lancement & connexion", s3_d: "Réservations, Google Maps et SEO branchés. On gère la technique.",
      s4_t: "Gérer & grandir", s4_d: "Modifiez la carte vous-même. On optimise et améliore chaque mois.",
    },
    lead: {
      eyebrow: "Commencer", title: "Deux façons de commencer.",
      lead: "Réservez un appel ou laissez un mot — nous répondons dans le jour ouvré.",
      calendly_t: "Réserver un appel", calendly_d: "30 minutes. Sans préparation.",
      open_calendly: "Ouvrir le calendrier",
      form_t: "Ou écrivez-nous",
      f_name: "Votre nom", f_email: "E-mail", f_restaurant: "Restaurant ou café",
      f_city: "Ville", f_country: "Pays", f_plan: "Intéressé par", f_message: "Que recherchez-vous ?",
      submit: "Envoyer", sending: "Envoi…",
      success_t: "Merci", success_d: "Message reçu — nous revenons vers vous sous un jour ouvré.",
      error: "Une erreur est survenue. Réessayez ou écrivez à Kshitij.shah@thevinculum.de.",
      no_pref: "Sans préférence",
    },
    cta: {
      eyebrow: "Quand vous voulez", title: "Votre restaurant mérite un site à sa hauteur.",
      sub: "Sans frais d'installation. Sans engagement. En ligne en 14 jours.", primary: "Réserver un appel",
    },
    footer: {
      tag: "Sites de marque, réservations et SEO pour restaurants et cafés indépendants en Europe du Nord et du Centre.",
      product: "Produit", company: "Société", legal: "Légal",
      l_why: "Pourquoi Vinculum", l_features: "Fonctionnalités", l_pricing: "Tarifs", l_how: "Comment ça marche",
      l_about: "À propos", l_contact: "Contact", l_book: "Réserver un appel",
      l_privacy: "Confidentialité", l_terms: "CGU", l_gdpr: "RGPD",
      copyright: "Vinculum. Conçu pour l'hôtellerie européenne.",
      markets: "Au service de l'Europe du Nord et du Centre — Pays-Bas, Allemagne, Belgique, France, Autriche, Danemark, Suède.",
    },
  },
  nl: {
    nav: { why: "Waarom", features: "Functies", pricing: "Prijzen", how: "Zo werkt het", markets: "Markten", cta: "Begin nu" },
    hero: {
      eyebrow: "Gebouwd voor restaurants & cafés in Europa",
      title_a: "Een website die uw",
      title_em: "restaurant",
      title_b: "even smaakvol laat aanvoelen als het eten.",
      sub: "Merkwebsites, reserveringen en live menu's — als abonnement. Geen opstartkosten. Live in 14 dagen. Maandelijks opzegbaar.",
      cta_primary: "Plan een intro-gesprek",
      cta_secondary: "Bekijk prijzen",
      stat1_v: "€0", stat1_l: "Opstartkosten",
      stat2_v: "14 dagen", stat2_l: "Tot u live bent",
      stat3_v: "0%", stat3_l: "Commissie op reserveringen",
    },
    why: {
      eyebrow: "Waarom een website telt",
      title: "Uw reputatie speelt zich niet meer af in de winkelstraat. Maar online.",
      lead: "Gasten beslissen vandaag waar ze eten vóór ze de deur uitgaan. Zonder eigen site geeft u die beslissing — en de marge — weg aan Deliveroo, TheFork en Google-reviews.",
      s1_v: "67%", s1_l: "van de gasten bestelt liever via de eigen site van het restaurant dan via Deliveroo of TheFork.",
      s2_v: "31%", s2_l: "van de Europese restaurants heeft nog geen website — een duidelijke voorsprong.",
      s3_v: "0%", s3_l: "commissie op reserveringen via uw Vinculum-site.",
    },
    features: {
      eyebrow: "Wat u krijgt", title: "Zes essentials. Eén abonnement.",
      lead: "Alles wat een onafhankelijk restaurant nodig heeft om geloofwaardig te zijn, gevonden te worden en de gastrelatie zelf in handen te houden.",
      f1_t: "Merkwebsite", f1_d: "Een redactionele, mobile-first site — geen template.",
      f2_t: "Online reserveringen", f2_d: "Directe boekingen. Nul commissie. Geen kosten per couvert meer.",
      f3_t: "Live menu-editor", f3_d: "Pas zelf gerechten, prijzen en allergenen aan — zonder ontwikkelaar.",
      f4_t: "SEO & lokale vindbaarheid", f4_d: "Google Maps, schema markup en lokale SEO.",
      f5_t: "Mobile-first", f5_d: "Acht op tien gasten komt via telefoon. Daarvoor ontwerpen we eerst.",
      f6_t: "AVG-proof", f6_d: "Cookie-toestemming, EU-hosting, privacy-by-design. Gebouwd voor Europese regels.",
    },
    pricing: {
      eyebrow: "Prijzen", title: "Eenvoudige maandprijzen. Geen opstartkosten.",
      lead: "Kies het pakket dat past bij waar u nu staat. Stap later op.",
      per_month: "/maand", most_popular: "Meest gekozen", cta: "Plan gesprek",
      launch_in: "Live in", days: "dagen",
      starter_t: "Starter", starter_d: "Een nette, geloofwaardige éénpaginasite voor wie vooral gevonden wil worden.",
      growth_t: "Growth", growth_d: "Reserveringen, meertalig en analytics — de complete digitale voordeur.",
      pro_t: "Pro", pro_d: "Multi-locatie ketens met POS, bezorgkoppelingen en eigen accountmanager.",
    },
    how: {
      eyebrow: "Zo werkt het", title: "Vier stappen, van keuken naar online.",
      s1_t: "Vertel ons over uw zaak", s1_d: "Een gesprek van 30 minuten. Uw verhaal, menu, foto's en publiek.",
      s2_t: "Wij ontwerpen uw site", s2_d: "Een redactioneel concept op uw domein, binnen dagen.",
      s3_t: "Lancering & koppelingen", s3_d: "Reserveringen, Google Maps en SEO ingericht. Wij doen de techniek.",
      s4_t: "Beheren & groeien", s4_d: "Wijzig het menu zelf. Wij optimaliseren en verbeteren maandelijks.",
    },
    lead: {
      eyebrow: "Beginnen", title: "Twee manieren om te starten.",
      lead: "Plan een gesprek in onze agenda of stuur een kort bericht — we reageren binnen één werkdag.",
      calendly_t: "Plan een gesprek", calendly_d: "30 minuten. Geen voorbereiding nodig.",
      open_calendly: "Open agenda",
      form_t: "Of stuur een bericht",
      f_name: "Uw naam", f_email: "E-mail", f_restaurant: "Restaurant of café",
      f_city: "Stad", f_country: "Land", f_plan: "Interesse in", f_message: "Waar bent u naar op zoek?",
      submit: "Bericht versturen", sending: "Versturen…",
      success_t: "Bedankt", success_d: "We hebben uw bericht ontvangen en reageren binnen één werkdag.",
      error: "Er ging iets mis. Probeer opnieuw of mail Kshitij.shah@thevinculum.de.",
      no_pref: "Geen voorkeur",
    },
    cta: {
      eyebrow: "Wanneer u zover bent", title: "Uw restaurant verdient een site die het recht doet.",
      sub: "Geen opstartkosten. Maandelijks opzegbaar. Live in 14 dagen.", primary: "Plan een intro-gesprek",
    },
    footer: {
      tag: "Merkwebsites, reserveringen en SEO voor onafhankelijke restaurants en cafés in Noord- en Midden-Europa.",
      product: "Product", company: "Bedrijf", legal: "Juridisch",
      l_why: "Waarom Vinculum", l_features: "Functies", l_pricing: "Prijzen", l_how: "Zo werkt het",
      l_about: "Over ons", l_contact: "Contact", l_book: "Plan gesprek",
      l_privacy: "Privacy", l_terms: "Voorwaarden", l_gdpr: "AVG",
      copyright: "Vinculum. Gebouwd voor de Europese horeca.",
      markets: "Voor Noord- & Midden-Europa — Nederland, Duitsland, België, Frankrijk, Oostenrijk, Denemarken, Zweden.",
    },
  },
};

const I18nContext = createContext({ locale: "en", setLocale: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const stored = localStorage.getItem("vinculum_locale");
      if (stored && dict[stored]) return stored;
    } catch {}
    return "en";
  });

  const setLocale = useCallback((code) => {
    if (!dict[code]) return;
    setLocaleState(code);
    try { localStorage.setItem("vinculum_locale", code); } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback((path) => {
    const parts = path.split(".");
    let node = dict[locale] || dict.en;
    for (const p of parts) {
      if (node == null) return path;
      node = node[p];
    }
    if (node == null) {
      // fallback to english
      let fb = dict.en;
      for (const p of parts) {
        if (fb == null) return path;
        fb = fb[p];
      }
      return fb ?? path;
    }
    return node;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
